/**
 * O campo de busca ao vivo no SGR (spec 032, §3.1): o campo da placa do "Novo
 * ticket" e o do Recurso do ticket. A regra (pausa, cancelamento,
 * estreitamento, memória do campo) mora no controlador puro
 * (sgr-search-controller, provado na API); aqui só a tela:
 *
 * - até 10 linhas: placa, modelo, nome do associado e situação; "Já no ERP";
 *   "pelo nome" quando casou por nome ou modelo; INATIVO e CANCELADO por
 *   último, em cinza, com aviso (resposta 3: nada bloqueia);
 * - CPF válido mostra as PESSOAS (CPF mascarado); escolher uma lista os
 *   veículos dela, com o aviso de homônimo;
 * - teclado: ↑/↓, Enter e Esc (fecha a lista, não o diálogo), como o
 *   autocompletar de endereço da cotação (029).
 *
 * Os textos não trazem palavra de vertical: o nome da origem vem da API e o
 * recurso, a placa e o associado vêm dos termos do tenant.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import type { SgrCustomerItem, SgrVehicleItem, Terms } from '@erp/shared';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/misc';
import { cn } from '@/lib/utils';
import {
  createSgrSearchController,
  IDLE_SGR_SEARCH,
  sgrSearchStatus,
  visibleSgrItems,
  type SgrSearchController,
  type SgrSearchState,
} from './sgr-search-controller';
import { sgrSearch } from './sgr-queries';

export function SgrVehicleSearch({
  id,
  value,
  onText,
  onPick,
  sourceName,
  terms,
  placeholder,
  disabled = false,
  autoFocus = false,
}: {
  id: string;
  /** o texto do campo (no "Novo ticket", é a própria placa da ficha) */
  value: string;
  onText: (text: string) => void;
  onPick: (item: SgrVehicleItem) => void;
  sourceName: string;
  terms: Pick<Terms, 'resource' | 'resourceId' | 'contact'>;
  placeholder?: string;
  disabled?: boolean;
  autoFocus?: boolean;
}) {
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const controller = useRef<SgrSearchController | null>(null);
  const [state, setState] = useState<SgrSearchState>(IDLE_SGR_SEARCH);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);

  controller.current ??= createSgrSearchController({ search: sgrSearch, onChange: setState });
  useEffect(() => () => controller.current?.dispose(), []);

  // Esc fecha a LISTA, não o diálogo (o Radix escuta o Esc no document)
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || document.activeElement !== inputRef.current) return;
      e.stopPropagation();
      e.preventDefault();
      setOpen(false);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open]);

  const result = state.result;
  const vehicles = result?.kind === 'vehicles' ? visibleSgrItems(result.items) : [];
  const customers = result?.kind === 'customers' ? visibleSgrItems(result.items) : [];
  const options: Array<{ kind: 'vehicle'; item: SgrVehicleItem } | { kind: 'customer'; item: SgrCustomerItem }> = [
    ...customers.map((item) => ({ kind: 'customer' as const, item })),
    ...vehicles.map((item) => ({ kind: 'vehicle' as const, item })),
  ];
  // o que está na lista vale para o texto de agora só quando a busca terminou
  const settled = state.phase === 'done';
  const showList = open && state.phase !== 'idle';

  const choose = (i: number) => {
    const opt = options[i];
    if (!opt) return;
    if (opt.kind === 'customer') {
      controller.current?.pickCustomer(opt.item.name);
      setHighlight(-1);
      return;
    }
    onPick(opt.item);
    setOpen(false);
    setHighlight(-1);
  };

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    const n = options.length;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (n === 0) return;
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => (e.key === 'ArrowDown' ? (h + 1) % n : h <= 0 ? n - 1 : h - 1));
    } else if (e.key === 'Enter') {
      if (open && settled && highlight >= 0 && highlight < n) {
        e.preventDefault();
        choose(highlight);
      }
    } else if (e.key === 'Tab') {
      setOpen(false);
    }
  }

  const optionId = (i: number) => `${listId}-opcao-${i}`;
  const message = sgrSearchStatus(state, sourceName, terms);

  return (
    <div className="relative">
      <Search className="pointer-events-none absolute top-2 left-2.5 size-4 text-muted-foreground" aria-hidden />
      <Input
        ref={inputRef}
        id={id}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        autoComplete="off"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={showList}
        aria-controls={showList ? listId : undefined}
        aria-activedescendant={showList && highlight >= 0 ? optionId(highlight) : undefined}
        className={cn('h-8 pl-8', state.spinner && 'pr-8')}
        onChange={(e) => {
          onText(e.target.value);
          controller.current?.input(e.target.value);
          setOpen(true);
          setHighlight(-1);
        }}
        onKeyDown={onKeyDown}
        onFocus={() => {
          if (state.phase !== 'idle') setOpen(true);
        }}
        onBlur={() => setOpen(false)}
      />
      {state.spinner ? (
        <span className="pointer-events-none absolute top-2 right-2.5" aria-hidden>
          <Spinner className="size-4" />
        </span>
      ) : null}
      {showList ? (
        <div
          className="absolute z-50 mt-1 w-full min-w-72 overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md"
          data-testid={`${id}-resultados`}
          onMouseDown={(e) => e.preventDefault()}
        >
          {state.owner && result?.kind === 'vehicles' ? (
            <p className="border-b px-3 py-1.5 text-xs text-muted-foreground">
              {`${capitalize(terms.resource)}s de ${state.owner}`}
              {result.homonyms ? (
                <span className="block text-amber-700 dark:text-amber-300">
                  {`Mais de uma pessoa com esse nome no ${sourceName} — confira o ${terms.resource} certo.`}
                </span>
              ) : null}
            </p>
          ) : null}
          <ul id={listId} role="listbox" aria-label={`Busca no ${sourceName}`} className={cn('max-h-80 overflow-y-auto py-1', !settled && 'opacity-60')}>
            {options.map((opt, i) => (
              <li
                key={opt.kind === 'vehicle' ? `v-${opt.item.sgrId}` : `c-${opt.item.cpfMasked}-${i}`}
                id={optionId(i)}
                role="option"
                aria-selected={i === highlight}
                onMouseDown={(e) => {
                  e.preventDefault();
                  if (settled) choose(i);
                }}
                onMouseEnter={() => setHighlight(i)}
                className={cn('cursor-pointer px-3 py-1.5 text-sm', i === highlight && 'bg-accent text-accent-foreground')}
              >
                {opt.kind === 'vehicle' ? <VehicleOption item={opt.item} /> : <CustomerOption item={opt.item} terms={terms} />}
              </li>
            ))}
          </ul>
          {message ? (
            <p
              role="status"
              className={cn(
                'flex items-center gap-2 border-t px-3 py-2 text-xs',
                message.tone === 'warn' ? 'text-amber-700 dark:text-amber-300' : 'text-muted-foreground'
              )}
            >
              {message.spinner ? <Spinner className="size-3.5" /> : null}
              {message.text}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

function VehicleOption({ item }: { item: SgrVehicleItem }) {
  return (
    <span className={cn('flex min-w-0 items-center gap-2', item.inactive && 'text-muted-foreground')}>
      <span className="shrink-0 font-semibold tabular-nums">{item.plate}</span>
      <span className="min-w-0 flex-1 truncate">
        {[item.model, item.ownerName].filter(Boolean).join(' · ')}
        {item.match === 'other' ? <span className="ml-1 text-xs text-muted-foreground">(pelo nome)</span> : null}
      </span>
      {item.equipmentId !== null ? (
        <span className="shrink-0 rounded-sm bg-primary/10 px-1.5 py-px text-[11px] font-medium text-primary">Já no ERP</span>
      ) : null}
      {item.status ? (
        <span
          className={cn(
            'shrink-0 rounded-sm px-1.5 py-px text-[11px] font-medium',
            item.inactive ? 'bg-muted text-muted-foreground' : 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
          )}
          title={item.inactive ? 'Situação inativa na origem — pode ser escolhido, confira antes' : undefined}
        >
          {item.status}
        </span>
      ) : null}
    </span>
  );
}

function CustomerOption({ item, terms }: { item: SgrCustomerItem; terms: Pick<Terms, 'contact'> }) {
  return (
    <span className="flex min-w-0 items-center gap-2">
      <span className="min-w-0 flex-1 truncate font-medium">{item.name}</span>
      <span className="shrink-0 text-xs text-muted-foreground tabular-nums">{item.cpfMasked}</span>
      {item.status ? <span className="shrink-0 text-[11px] text-muted-foreground">{item.status}</span> : null}
      <span className="sr-only">{`(${terms.contact})`}</span>
    </span>
  );
}
